/**
 * WP-06B — Authenticated server lifecycle entry point for Loop lifecycle commands.
 *
 * The actor is NEVER accepted from the caller. `requireSupabaseAuth` validates
 * the request bearer token against Supabase Auth and puts the verified `userId`
 * on the context; that value alone becomes the lifecycle actor. The client may
 * offer only the Loop id, its optimistic version expectation, and the governed
 * request body, all validated in `./lifecycleRequest` before they reach the
 * command.
 *
 * The request identity is constructed HERE, after verification, as the trusted
 * deterministic identity. No client value can influence it, so an interrupted
 * mutation that is retried replays instead of applying twice.
 *
 * The privileged adapter is imported INSIDE the handler so the service-role
 * client never enters a client-reachable module graph.
 *
 * The response is the stable, non-sensitive outcome vocabulary: on failure a
 * reason code and a retry hint, never a provider message, SQL text, or raw
 * exception.
 */

import { createServerFn } from "@tanstack/react-start";

import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { applyLoopLifecycleCommand, type LoopLifecycleOutcome } from "./lifecycleCommand";
import { validateLifecycleInput } from "./lifecycleRequest";
import { DETERMINISTIC_REQUEST_IDENTITY } from "./requestIdentity";

export const runLoopLifecycleCommand = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(validateLifecycleInput)
  .handler(async ({ data, context }): Promise<LoopLifecycleOutcome> => {
    const { supabaseLoopLifecyclePort } = await import("./lifecycleAdapter.server");
    return applyLoopLifecycleCommand(
      supabaseLoopLifecyclePort,
      { userId: context.userId },
      data.loopId,
      data.expectedVersion,
      data.request,
      DETERMINISTIC_REQUEST_IDENTITY,
    );
  });
